import {Component, Input, OnInit} from '@angular/core';
import {
    Address,
    BaseStepperFormComponent,
    DonationProgram,
    DonationProgramService,
    DonationProgramStatus,
    FileUploadService,
    FileUploadType,
    LoadingService,
    Organization,
    Person,
    ToastService,
    UploadImageModalComponent
} from 'brewbill-lib';
import {ModalController, Platform} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';

@Component({
    selector: 'bb-edit-donation-program',
    templateUrl: './edit-donation-program.component.html',
    styleUrls: ['./edit-donation-program.component.scss'],
})
export class EditDonationProgramComponent extends BaseStepperFormComponent implements OnInit {
    @Input() donationProgram: DonationProgram;
    @Input() organization: Organization;
    formGroup: FormGroup;
    addressFormGroup: FormGroup;
    personFormGroup: FormGroup;

    constructor(
        modalController: ModalController,
        private donationProgramService: DonationProgramService,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private formBuilder: FormBuilder,
        private platform: Platform,
        private fileUploadService: FileUploadService
    ) {
        super(modalController);
    }

    ngOnInit() {
        const donationProgram = !!this.donationProgram ? this.donationProgram : new DonationProgram({});
        if (!this.donationProgram) {
            donationProgram.roundUp = true;
        }
        const presets = !!donationProgram.presets ? donationProgram.presets.split(',') : [];
        this.formGroup = this.formBuilder.group(
            {
                id: donationProgram.id,
                parentId: this.organization.id,
                name: [donationProgram.name, Validators.required],
                description: donationProgram.description,
                roundUp: donationProgram.roundUp,
                statusCheck: !donationProgram.status || donationProgram.status === DonationProgramStatus.ACTIVE,
                imageAddress: donationProgram.imageAddress,
                color: [!!donationProgram.color ? donationProgram.color : '#fddb31', Validators.required],
                contrastColor: [!!donationProgram.contrastColor ? donationProgram.contrastColor : '#000000', Validators.required],
                preset1: [presets.length > 0 ? presets[0] : null, [Validators.min(0), Validators.max(100)]],
                preset2: [presets.length > 1 ? presets[1] : null, [Validators.min(0), Validators.max(100)]],
                preset3: [presets.length > 2 ? presets[2] : null, [Validators.min(0), Validators.max(100)]],
                preset4: [presets.length > 3 ? presets[3] : null, [Validators.min(0), Validators.max(100)]]
            }
        );
    }

    setValue(field: string, val: any) {
        this.formGroup.get(field).setValue(val);
    }

    async openUploadImageModal() {
        if (this.platform.is('desktop')) {
            const modal = await this.modalController.create({
                component: UploadImageModalComponent,
                cssClass: 'menu-modal'
            });

            modal.onDidDismiss().then(async (dataReturned) => {
                if (dataReturned != null && dataReturned.data != null) {
                    this.uploadImage(dataReturned.data);
                }
            });

            await modal.present();
        } else {
            const image = await this.fileUploadService.getPhoto();
            this.loadingService.present();
            const imgBlob = this.fileUploadService.b64toBlob(image.base64String, `image/${image.format}`);
            this.uploadImage(imgBlob);
        }
    }

    uploadImage(data: any) {
        this.loadingService.present();
        this.fileUploadService.uploadImage(this.organization.id, FileUploadType.DONATION_IMAGE, data)
            .subscribe(async (imageAddress: any) => {
                this.formGroup.controls.imageAddress.setValue(imageAddress.response);
                this.loadingService.dismiss();
            }, async (err) => {
                console.log(err);
                this.loadingService.dismiss();
                await this.toastService.error('Error uploading image.');
            });
    }

    removeImage(event) {
        event.stopPropagation();
        this.formGroup.controls.imageAddress.setValue(null);
    }

    personFormInitialized(form: FormGroup) {
        this.personFormGroup = form;
    }

    addressFormInitialized(form: FormGroup) {
        this.addressFormGroup = form;
    }

    onSubmit() {
        this.loadingService.present();

        const presets = [];
        if (!!this.formGroup.controls.preset1.value || this.formGroup.controls.preset1.value === 0) {
            presets.push(Number.parseFloat(this.formGroup.controls.preset1.value));
        }
        if (!!this.formGroup.controls.preset2.value || this.formGroup.controls.preset2.value === 0) {
            presets.push(Number.parseFloat(this.formGroup.controls.preset2.value));
        }
        if (!!this.formGroup.controls.preset3.value || this.formGroup.controls.preset3.value === 0) {
            presets.push(Number.parseFloat(this.formGroup.controls.preset3.value));
        }
        if (!!this.formGroup.controls.preset4.value || this.formGroup.controls.preset4.value === 0) {
            presets.push(Number.parseFloat(this.formGroup.controls.preset4.value));
        }

        const donationProgram = new DonationProgram({...this.formGroup.value});
        donationProgram.presets = presets.sort((a, b) => {
            return a - b;
        }).join();

        donationProgram.status = this.formGroup.controls.statusCheck.value ? DonationProgramStatus.ACTIVE : DonationProgramStatus.INACTIVE;
        donationProgram.contact = new Person(this.personFormGroup.value);
        donationProgram.address = new Address(this.addressFormGroup.value);
        if (!donationProgram.id) {
            this.donationProgramService.create(donationProgram).subscribe(async d => {
                this.loadingService.dismiss();
                await this.close(d);
            });
        } else {
            this.donationProgramService.update(donationProgram).subscribe(async d => {
                this.loadingService.dismiss();
                await this.close(d);
            });
        }
    }
}
